import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { FaBell, FaEnvelope, FaSms, FaWhatsapp, FaTelegramPlane, FaSave, FaPaperPlane, FaEdit, FaUndo, FaSyncAlt, FaExternalLinkAlt } from 'react-icons/fa';
import { notificationsAPI, rolesAPI, usersAPI } from '../services/api';
import { Modal, ModalFooter } from '../components/Modals';
import FloatingScrollWrapper from '../components/FloatingScrollWrapper';

// The delivery log's `module` column is the same MODULE string statusAlerts()
// is configured with in backend/server.js (the middleware that actually fires
// these alerts) — map it to the screen that owns the record, same idea as
// AuditTrail.js's ENTITY_ROUTES but keyed on module instead of table_name.
const MODULE_ROUTES = {
  RESULTS: (id) => ({ path: '/results', state: id ? { openViewResultId: id } : undefined }),
  PRODUCTS: (id) => ({ path: '/product-master', state: id ? { openViewProductId: id } : undefined }),
  API_MASTER: () => ({ path: '/api-master' }),
  EQUIPMENT: (id) => ({ path: '/equipment-master', state: id ? { openViewEquipmentId: id } : undefined }),
  EQUIPMENT_TRAINS: (id) => ({ path: '/equipment-trains', state: id ? { openViewTrainId: id } : undefined }),
  CLEANING_CONFIG: () => ({ path: '/cleaning-config' }),
  ANALYTICAL_METHODS: () => ({ path: '/analytical-methods' }),
  ACCEPTANCE_CRITERIA: () => ({ path: '/acceptance-criteria' }),
  VRL: () => ({ path: '/vrl' }),
  MACO: () => ({ path: '/maco' }),
  SAMPLING_PLANS: () => ({ path: '/sampling-plans' }),
  TRAINING: () => ({ path: '/training' }),
  CCV: () => ({ path: '/ccv' }),
  REVALIDATION: () => ({ path: '/revalidation' }),
  DOCUMENTS: () => ({ path: '/documents' }),
  RISK_ASSESSMENT: () => ({ path: '/risk-assessment' }),
};
const moduleTarget = (module, recordId) => (MODULE_ROUTES[module] ? MODULE_ROUTES[module](recordId) : null);

const CHANNEL_META = {
  email: { label: 'Email', icon: FaEnvelope, contact: 'user email address' },
  sms: { label: 'SMS', icon: FaSms, contact: 'user phone number' },
  whatsapp: { label: 'WhatsApp', icon: FaWhatsapp, contact: 'WhatsApp number (or phone number)' },
  telegram: { label: 'Telegram', icon: FaTelegramPlane, contact: 'Telegram chat ID' },
};
const CHANNELS = Object.keys(CHANNEL_META);

const SECRET_LABELS = {
  password: 'SMTP password',
  twilio_auth_token: 'Twilio auth token',
  http_api_key: 'Gateway API key',
  meta_access_token: 'Meta access token',
  bot_token: 'Bot token',
};

const Field = ({ label, hint, children }) => (
  <div className="form-group" style={{ marginBottom: '10px' }}>
    <label className="form-label">{label}</label>
    {children}
    {hint && <small className="text-muted" style={{ display: 'block', marginTop: '3px' }}>{hint}</small>}
  </div>
);

// Renders {{placeholders}} with sample values so the template editor shows a live preview.
const SAMPLE = {
  recipient_name: 'Manoj Pawar', module: 'Cleaning Config', record: 'U1-LINE-A-OCB / Change parts', record_id: 42,
  status: 'Pending Approval', previous_status: 'Draft', action_by: 'QA Officer', comment: 'Please review',
  due_date: '22 Oct 2026', link: 'https://clean.primegmp.com/cleaning-config', app_name: 'AiventraClean', date_time: '15 Oct 2026, 2:30 pm',
};
const renderSample = (text) => String(text || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, k) => (SAMPLE[k] !== undefined ? String(SAMPLE[k]) : `{{${k}}}`));

const ChannelCard = ({ channel, state, onSaved, users }) => {
  const meta = CHANNEL_META[channel];
  const Icon = meta.icon;
  const [enabled, setEnabled] = useState(!!state?.enabled);
  const [config, setConfig] = useState(state?.config || {});
  const [secrets, setSecrets] = useState({});
  const [saving, setSaving] = useState(false);
  const [testUser, setTestUser] = useState('');
  const [testing, setTesting] = useState(false);

  useEffect(() => {
    setEnabled(!!state?.enabled);
    setConfig(state?.config || {});
    setSecrets({});
  }, [state]);

  const setC = (k, v) => setConfig((c) => ({ ...c, [k]: v }));
  const secretInput = (k) => (
    <Field label={SECRET_LABELS[k] || k} hint={state?.secrets_masked?.[k] ? `Stored: ${state.secrets_masked[k]} — leave blank to keep` : 'Not set'}>
      <input className="form-control" type="password" autoComplete="new-password" value={secrets[k] || ''} onChange={(e) => setSecrets((s) => ({ ...s, [k]: e.target.value }))} />
    </Field>
  );

  const save = async () => {
    setSaving(true);
    try {
      await notificationsAPI.saveChannel(channel, { enabled, config, secrets });
      toast.success(`${meta.label} settings saved`);
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const test = async () => {
    setTesting(true);
    try {
      const res = await notificationsAPI.testChannel(channel, { user_id: testUser || undefined });
      if (res.data.success) toast.success(res.data.message);
      else toast.error(res.data.message || 'Test failed');
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Test failed');
    } finally {
      setTesting(false);
    }
  };

  const provider = config.provider || (channel === 'whatsapp' ? 'meta' : 'twilio');

  return (
    <div className="card" style={{ marginBottom: 0 }}>
      <div className="card-header" style={{ justifyContent: 'space-between' }}>
        <h3 className="card-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><Icon /> {meta.label}</h3>
        <label style={{ display: 'flex', alignItems: 'center', gap: '6px', fontWeight: 600 }}>
          <input type="checkbox" checked={enabled} onChange={(e) => setEnabled(e.target.checked)} /> Enabled
        </label>
      </div>
      <div className="card-body">
        {channel === 'email' && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 90px', gap: '8px' }}>
              <Field label="SMTP host"><input className="form-control" value={config.host || ''} onChange={(e) => setC('host', e.target.value)} placeholder="smtp.office365.com" /></Field>
              <Field label="Port"><input className="form-control" type="number" value={config.port || ''} onChange={(e) => setC('port', e.target.value)} placeholder="587" /></Field>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '10px' }}>
              <input type="checkbox" checked={config.secure === true || config.secure === 'true'} onChange={(e) => setC('secure', e.target.checked)} /> Use SSL/TLS (port 465)
            </label>
            <Field label="Username"><input className="form-control" value={config.username || ''} onChange={(e) => setC('username', e.target.value)} /></Field>
            {secretInput('password')}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px' }}>
              <Field label="From address"><input className="form-control" value={config.from_address || ''} onChange={(e) => setC('from_address', e.target.value)} placeholder="alerts@company.com" /></Field>
              <Field label="From name"><input className="form-control" value={config.from_name || ''} onChange={(e) => setC('from_name', e.target.value)} placeholder="AiventraClean" /></Field>
            </div>
          </>
        )}

        {channel === 'sms' && (
          <>
            <Field label="Provider">
              <select className="form-control" value={provider} onChange={(e) => setC('provider', e.target.value)}>
                <option value="twilio">Twilio</option>
                <option value="http">Other SMS gateway (HTTP API — MSG91, Fast2SMS, Textlocal…)</option>
              </select>
            </Field>
            {provider === 'http' ? (
              <>
                <Field label="Method">
                  <select className="form-control" value={config.http_method || 'GET'} onChange={(e) => setC('http_method', e.target.value)}>
                    <option value="GET">GET</option><option value="POST">POST (JSON body)</option>
                  </select>
                </Field>
                <Field label="Gateway URL" hint="Use {{to}}, {{message}} and {{api_key}} — they are filled in and URL-encoded at send time.">
                  <input className="form-control" value={config.http_url || ''} onChange={(e) => setC('http_url', e.target.value)} placeholder="https://api.gateway.com/send?apikey={{api_key}}&to={{to}}&msg={{message}}" />
                </Field>
                {(config.http_method || 'GET') === 'POST' && (
                  <Field label="JSON body template">
                    <textarea className="form-control" rows={3} value={config.http_body || ''} onChange={(e) => setC('http_body', e.target.value)} placeholder='{"to": "{{to}}", "message": "{{message}}"}' />
                  </Field>
                )}
                <Field label="Headers (JSON, optional)" hint='e.g. {"authorization": "{{api_key}}"}'>
                  <input className="form-control" value={config.http_headers || ''} onChange={(e) => setC('http_headers', e.target.value)} />
                </Field>
                {secretInput('http_api_key')}
              </>
            ) : (
              <>
                <Field label="Account SID"><input className="form-control" value={config.twilio_account_sid || ''} onChange={(e) => setC('twilio_account_sid', e.target.value)} /></Field>
                {secretInput('twilio_auth_token')}
                <Field label="From number"><input className="form-control" value={config.twilio_from || ''} onChange={(e) => setC('twilio_from', e.target.value)} placeholder="+1415XXXXXXX" /></Field>
              </>
            )}
            <small className="text-muted">In India, SMS to users needs a DLT-registered sender ID and template on the gateway side.</small>
          </>
        )}

        {channel === 'whatsapp' && (
          <>
            <Field label="Provider">
              <select className="form-control" value={provider} onChange={(e) => setC('provider', e.target.value)}>
                <option value="meta">WhatsApp Cloud API (Meta)</option>
                <option value="twilio">Twilio WhatsApp</option>
              </select>
            </Field>
            {provider === 'twilio' ? (
              <>
                <Field label="Account SID"><input className="form-control" value={config.twilio_account_sid || ''} onChange={(e) => setC('twilio_account_sid', e.target.value)} /></Field>
                {secretInput('twilio_auth_token')}
                <Field label="WhatsApp sender"><input className="form-control" value={config.twilio_from || ''} onChange={(e) => setC('twilio_from', e.target.value)} placeholder="+14155238886" /></Field>
              </>
            ) : (
              <>
                <Field label="Phone number ID"><input className="form-control" value={config.meta_phone_number_id || ''} onChange={(e) => setC('meta_phone_number_id', e.target.value)} /></Field>
                <Field label="Graph API version"><input className="form-control" value={config.meta_api_version || ''} onChange={(e) => setC('meta_api_version', e.target.value)} placeholder="v21.0" /></Field>
                {secretInput('meta_access_token')}
              </>
            )}
            <small className="text-muted">WhatsApp only delivers free-text messages to users who messaged your business number in the last 24 hours. For guaranteed delivery, register the template text with Meta / Twilio as an approved template.</small>
          </>
        )}

        {channel === 'telegram' && (
          <>
            {secretInput('bot_token')}
            <small className="text-muted">Create a bot with @BotFather to get the token. Each user must open the bot and press Start, then their chat ID (from @userinfobot) is entered in User Management → Edit User → Alerts.</small>
          </>
        )}

        <div style={{ display: 'flex', gap: '8px', alignItems: 'flex-end', flexWrap: 'wrap', marginTop: '14px', paddingTop: '12px', borderTop: '1px solid var(--border-color)' }}>
          <button className="btn btn-primary" onClick={save} disabled={saving}><FaSave /> {saving ? 'Saving…' : 'Save'}</button>
          <div style={{ flex: 1 }} />
          <select className="form-control" style={{ width: '190px' }} value={testUser} onChange={(e) => setTestUser(e.target.value)} title="Send the test to">
            <option value="">Test to: myself</option>
            {users.map((u) => <option key={u.user_id} value={u.user_id}>Test to: {u.employee_name || u.username}</option>)}
          </select>
          <button className="btn btn-outline" onClick={test} disabled={testing} title={`Sends the "Approval task" template to the user's ${meta.contact}. Save first.`}>
            <FaPaperPlane /> {testing ? 'Sending…' : 'Send test'}
          </button>
        </div>
      </div>
    </div>
  );
};

const TemplateModal = ({ template, event, placeholders, onClose, onSaved }) => {
  const [subject, setSubject] = useState(template.subject || '');
  const [body, setBody] = useState(template.body || '');
  const [saving, setSaving] = useState(false);
  const isEmail = template.channel === 'email';

  const insert = (key) => setBody((b) => `${b}{{${key}}}`);

  const save = async () => {
    setSaving(true);
    try {
      await notificationsAPI.saveTemplate(template.event_key, template.channel, { subject, body });
      toast.success('Template saved');
      onSaved();
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const reset = async () => {
    if (!window.confirm('Replace this template with the original default text?')) return;
    try {
      const res = await notificationsAPI.resetTemplate(template.event_key, template.channel);
      setSubject(res.data.data.subject || '');
      setBody(res.data.data.body || '');
      toast.success('Default template restored');
      onSaved(false);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Reset failed');
    }
  };

  return (
    <Modal isOpen size="lg" title={`Edit template — ${event?.label || template.event_key} · ${CHANNEL_META[template.channel].label}`} onClose={onClose}>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(0, 1.2fr) minmax(0, 1fr)', gap: '16px' }}>
        <div>
          {isEmail && (
            <Field label="Subject"><input className="form-control" value={subject} onChange={(e) => setSubject(e.target.value)} /></Field>
          )}
          <Field label="Message" hint={template.channel === 'telegram' ? 'Telegram supports <b>bold</b> and <i>italic</i> HTML tags.' : template.channel === 'whatsapp' ? 'WhatsApp supports *bold* and _italic_.' : template.channel === 'sms' ? 'Keep SMS short — long messages are split and billed per 160 characters.' : ''}>
            <textarea className="form-control" rows={14} value={body} onChange={(e) => setBody(e.target.value)} style={{ fontFamily: 'monospace', fontSize: 'var(--fs-md)' }} />
          </Field>
          {template.channel === 'sms' && <small className="text-muted">{renderSample(body).length} characters with sample data</small>}
          <div style={{ marginTop: '10px' }}>
            <div className="form-label">Insert placeholder</div>
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
              {placeholders.map((p) => (
                <button key={p.key} type="button" className="btn btn-sm btn-outline" title={p.label} onClick={() => insert(p.key)}>{`{{${p.key}}}`}</button>
              ))}
            </div>
          </div>
        </div>
        <div>
          <div className="form-label">Preview (sample data)</div>
          <div style={{ border: '1px solid var(--border-color)', borderRadius: '8px', padding: '12px', background: 'var(--bg-secondary, var(--card-bg))', whiteSpace: 'pre-wrap', fontSize: 'var(--fs-md)', minHeight: '200px' }}>
            {isEmail && <div style={{ fontWeight: 700, marginBottom: '8px', borderBottom: '1px solid var(--border-color)', paddingBottom: '6px' }}>{renderSample(subject)}</div>}
            {renderSample(body)}
          </div>
        </div>
      </div>
      <ModalFooter>
        <button className="btn btn-outline" onClick={reset}><FaUndo /> Restore default</button>
        <div style={{ flex: 1 }} />
        <button className="btn btn-outline" onClick={onClose}>Cancel</button>
        <button className="btn btn-primary" onClick={save} disabled={saving}><FaSave /> {saving ? 'Saving…' : 'Save template'}</button>
      </ModalFooter>
    </Modal>
  );
};

const AlertsNotifications = () => {
  const navigate = useNavigate();
  const [tab, setTab] = useState('channels');
  const [settings, setSettings] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [roles, setRoles] = useState([]);
  const [users, setUsers] = useState([]);
  const [general, setGeneral] = useState({ app_name: '', app_base_url: '', notify_actor: false });
  const [events, setEvents] = useState([]);
  const [editing, setEditing] = useState(null);
  const [log, setLog] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    try {
      const [s, t] = await Promise.all([notificationsAPI.getSettings(), notificationsAPI.getTemplates()]);
      setSettings(s.data.data);
      setGeneral({ app_name: '', app_base_url: '', notify_actor: false, ...(s.data.data.general || {}) });
      setEvents(s.data.data.events || []);
      setTemplates(t.data.data || []);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Failed to load alert settings');
    } finally {
      setLoading(false);
    }
  }, []);

  const loadLog = useCallback(async () => {
    try { setLog((await notificationsAPI.getLog(200)).data.data || []); } catch { setLog([]); }
  }, []);

  useEffect(() => {
    load();
    rolesAPI.getAll().then((r) => setRoles((r.data.data || []).filter((x) => x.is_active !== false).map((x) => x.role_name))).catch(() => {});
    usersAPI.getAll().then((r) => setUsers((r.data.data || r.data || []).filter((u) => u.is_active !== false))).catch(() => {});
  }, [load]);
  useEffect(() => { if (tab === 'log') loadLog(); }, [tab, loadLog]);

  const templateMap = useMemo(() => {
    const m = {};
    templates.forEach((t) => { m[`${t.event_key}:${t.channel}`] = t; });
    return m;
  }, [templates]);

  const saveGeneral = async () => {
    try { await notificationsAPI.saveGeneral(general); toast.success('General alert settings saved'); } catch (err) { toast.error(err?.response?.data?.message || 'Save failed'); }
  };

  const updateEvent = (key, patch) => setEvents((list) => list.map((e) => (e.event_key === key ? { ...e, ...patch } : e)));
  const saveEvent = async (ev) => {
    try {
      await notificationsAPI.saveEvent(ev.event_key, ev);
      toast.success(`"${ev.label}" saved`);
    } catch (err) {
      toast.error(err?.response?.data?.message || 'Save failed');
    }
  };

  if (loading) return <div className="loading-container"><div className="spinner" /></div>;

  return (
    <div>
      <div style={{ marginBottom: '16px' }}>
        <h2 className="page-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}><FaBell /> Alerts &amp; Notifications</h2>
        <p className="page-subtitle">Send task and approval alerts to users by Email, SMS, WhatsApp and Telegram. Every message uses an editable template.</p>
      </div>

      <div className="lms-tabs" style={{ marginBottom: '16px' }}>
        {[['channels', 'Channels'], ['events', 'Alert Events & Recipients'], ['templates', 'Templates'], ['log', 'Delivery Log']].map(([k, l]) => (
          <button key={k} type="button" className={`lms-tab ${tab === k ? 'active' : ''}`} onClick={() => setTab(k)}>{l}</button>
        ))}
      </div>

      {tab === 'channels' && (
        <>
          <div className="card" style={{ marginBottom: '16px' }}>
            <div className="card-header"><h3 className="card-title">General</h3></div>
            <div className="card-body" style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(240px, 1fr))', gap: '12px', alignItems: 'end' }}>
              <Field label="Application name ({{app_name}})"><input className="form-control" value={general.app_name || ''} onChange={(e) => setGeneral((g) => ({ ...g, app_name: e.target.value }))} /></Field>
              <Field label="Application URL (for {{link}})" hint="e.g. https://clean.primegmp.com"><input className="form-control" value={general.app_base_url || ''} onChange={(e) => setGeneral((g) => ({ ...g, app_base_url: e.target.value }))} /></Field>
              <label style={{ display: 'flex', alignItems: 'center', gap: '6px', marginBottom: '14px' }}>
                <input type="checkbox" checked={!!general.notify_actor} onChange={(e) => setGeneral((g) => ({ ...g, notify_actor: e.target.checked }))} />
                Also alert the user who performed the action
              </label>
              <div style={{ marginBottom: '10px' }}><button className="btn btn-primary" onClick={saveGeneral}><FaSave /> Save</button></div>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(420px, 1fr))', gap: '16px' }}>
            {CHANNELS.map((c) => <ChannelCard key={c} channel={c} state={settings?.channels?.[c]} onSaved={load} users={users} />)}
          </div>
        </>
      )}

      {tab === 'events' && (
        <div className="card">
          <div className="card-body">
            <p className="text-muted" style={{ marginTop: 0 }}>
              Alerts fire automatically when a record's status changes in any module: submitted / pending approval, approved, or rejected / returned to draft. They also fire when a training is assigned.
              Approval tasks go <strong>level by level</strong>. Every active user holding the role of the level now waiting is alerted: the configured approval-chain level, or the Product / QA sign-off stage. When that level signs, the next level is alerted.
              Contact details (email, phone, WhatsApp number, Telegram chat ID) and per-user channel opt-outs are set in User Management → Edit User.
            </p>
            <FloatingScrollWrapper>
              <table>
                <thead>
                  <tr><th>Alert</th><th>On</th><th title="For approval tasks these are only used when no approval level / stage applies">Send to roles</th><th>Record owner</th><th>Assigned user</th><th /></tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.event_key}>
                      <td style={{ minWidth: '220px' }}><strong>{ev.label}</strong><div className="text-muted" style={{ fontSize: 'var(--fs-sm)' }}>{ev.description}</div></td>
                      <td><input type="checkbox" checked={!!ev.enabled} onChange={(e) => updateEvent(ev.event_key, { enabled: e.target.checked })} /></td>
                      <td style={{ minWidth: '300px' }}>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px 12px' }}>
                          {roles.map((r) => (
                            <label key={r} style={{ display: 'flex', alignItems: 'center', gap: '4px', fontSize: 'var(--fs-md)' }}>
                              <input
                                type="checkbox"
                                checked={(ev.recipient_roles || []).includes(r)}
                                onChange={(e) => updateEvent(ev.event_key, { recipient_roles: e.target.checked ? [...(ev.recipient_roles || []), r] : (ev.recipient_roles || []).filter((x) => x !== r) })}
                              /> {r}
                            </label>
                          ))}
                        </div>
                      </td>
                      <td title="The user who created / submitted the record"><input type="checkbox" checked={!!ev.notify_owner} onChange={(e) => updateEvent(ev.event_key, { notify_owner: e.target.checked })} /></td>
                      <td title="The user a training is assigned to"><input type="checkbox" checked={!!ev.notify_assignee} onChange={(e) => updateEvent(ev.event_key, { notify_assignee: e.target.checked })} /></td>
                      <td><button className="btn btn-sm btn-primary" onClick={() => saveEvent(ev)}><FaSave /> Save</button></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </FloatingScrollWrapper>
          </div>
        </div>
      )}

      {tab === 'templates' && (
        <div className="card">
          <div className="card-body">
            <FloatingScrollWrapper>
              <table>
                <thead>
                  <tr><th>Alert</th>{CHANNELS.map((c) => <th key={c}>{CHANNEL_META[c].label}</th>)}</tr>
                </thead>
                <tbody>
                  {events.map((ev) => (
                    <tr key={ev.event_key}>
                      <td style={{ minWidth: '200px' }}><strong>{ev.label}</strong></td>
                      {CHANNELS.map((c) => {
                        const t = templateMap[`${ev.event_key}:${c}`];
                        return (
                          <td key={c} style={{ minWidth: '220px', verticalAlign: 'top' }}>
                            {t ? (
                              <>
                                <div style={{ fontSize: 'var(--fs-sm)', color: 'var(--text-secondary)', whiteSpace: 'pre-wrap', maxHeight: '64px', overflow: 'hidden', marginBottom: '6px' }}>
                                  {c === 'email' ? t.subject : t.body}
                                </div>
                                <button className="btn btn-sm btn-outline" onClick={() => setEditing(t)}><FaEdit /> Edit</button>
                              </>
                            ) : '—'}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                </tbody>
              </table>
            </FloatingScrollWrapper>
          </div>
        </div>
      )}

      {tab === 'log' && (
        <div className="card">
          <div className="card-header" style={{ justifyContent: 'space-between' }}>
            <h3 className="card-title">Last 200 messages</h3>
            <button className="btn btn-sm btn-outline" onClick={loadLog}><FaSyncAlt /> Refresh</button>
          </div>
          <div className="card-body">
            <FloatingScrollWrapper>
              <table>
                <thead><tr><th>Date / time</th><th>Alert</th><th>Channel</th><th>User</th><th>Sent to</th><th>Module / record</th><th>Status</th><th>Error</th></tr></thead>
                <tbody>
                  {log.length === 0 ? (
                    <tr><td colSpan={8} className="text-center">No messages sent yet</td></tr>
                  ) : log.map((l) => (
                    <tr key={l.id}>
                      <td style={{ whiteSpace: 'nowrap' }}>{new Date(l.created_at).toLocaleString()}</td>
                      <td>{events.find((e) => e.event_key === l.event_key)?.label || l.event_key}</td>
                      <td>{CHANNEL_META[l.channel]?.label || l.channel}</td>
                      <td>{l.user_name || '—'}</td>
                      <td>{l.recipient || '—'}</td>
                      <td>
                        {l.module === 'TEST' ? 'Test message' : (() => {
                          const label = `${l.module || ''}${l.record_id ? ` #${l.record_id}` : ''}`;
                          const target = l.module ? moduleTarget(l.module, l.record_id) : null;
                          return target
                            ? <button type="button" className="link-button" onClick={() => navigate(target.path, target.state ? { state: target.state } : undefined)} style={{ border: 'none', background: 'none', cursor: 'pointer', color: 'var(--primary-color)', padding: 0, display: 'inline-flex', alignItems: 'center', gap: '4px' }}>{label} <FaExternalLinkAlt size={10} /></button>
                            : label;
                        })()}
                      </td>
                      <td><span className={`badge ${l.status === 'SENT' ? 'badge-success' : 'badge-danger'}`}>{l.status}</span></td>
                      <td style={{ maxWidth: '320px', fontSize: 'var(--fs-sm)', color: 'var(--danger-text)' }}>{l.error || ''}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </FloatingScrollWrapper>
          </div>
        </div>
      )}

      {editing && (
        <TemplateModal
          template={editing}
          event={events.find((e) => e.event_key === editing.event_key)}
          placeholders={settings?.placeholders || []}
          onClose={() => setEditing(null)}
          onSaved={(close = true) => { load(); if (close) setEditing(null); }}
        />
      )}
    </div>
  );
};

export default AlertsNotifications;
